import ConfirmDeleteModal from '@/components/confirm-delete';
import {
  Text,
  TouchableOpacity,
  View,
  TextInput
} from '@/components/react-native';
import {
  approveComment,
  rejectComment,
  deleteCommentForNote,
  updateCommentForNote,
  getCommentsByNoteId,
  validateAndUpdateCommentCount
} from '@/firebase/airport-note';
import {useAppSelector} from '@/hooks/redux';
import {UserRole} from '@/services/tmm';
import useAirportNote from '@/stores/airport-note';
import {ICommentInfo, INoteInfo, ECommentStatus} from '@/types/airport';
import classNames from 'classnames';
import moment from 'moment';
import {useState, useEffect} from 'react';
import {ScrollView} from 'react-native';

interface NoteDetailCommentsProps {
  noteId: string;
  noteItem?: INoteInfo;
  preview?: boolean;
}

interface CommentItemProps {
  comment: ICommentInfo;
  noteItem: INoteInfo;
  onEdit: (comment: ICommentInfo) => void;
  onRefresh: () => void;
  preview?: boolean;
}

const CommentItem = ({
  comment,
  noteItem,
  onEdit,
  onRefresh,
  preview
}: CommentItemProps) => {
  const user = useAppSelector((state) => state.user.user);
  const airportNoteInfo = useAirportNote((state) => state.airportNoteInfo);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  // Check if user is admin (can manage comments)
  const isAdmin = true;
  const canEdit = !preview;
  const canManage = !preview;

  const handleApprove = async () => {
    if (!user?.user_name || !airportNoteInfo?.icao_code) return;
    setIsProcessing(true);
    try {
      await approveComment(noteItem.id, comment.id, 'Admin');
      onRefresh();
    } catch (error) {
      console.error('Error approving comment:', error);
      // TODO: Show error toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!user?.user_name || !airportNoteInfo?.icao_code) return;
    setIsProcessing(true);
    try {
      await rejectComment(noteItem.id, comment.id, 'Admin');
      onRefresh();
    } catch (error) {
      console.error('Error rejecting comment:', error);
      // TODO: Show error toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async () => {
    if (!airportNoteInfo?.icao_code) return;
    setIsProcessing(true);
    try {
      await deleteCommentForNote(
        airportNoteInfo.icao_code,
        noteItem.id,
        comment.id
      );
      onRefresh();
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error removing comment:', error);
      // TODO: Show error toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditText(comment.content);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || !airportNoteInfo?.icao_code) return;
    setIsProcessing(true);
    try {
      await updateCommentForNote(noteItem.id, comment.id, {
        content: editText.trim()
      });
      onRefresh();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
      // TODO: Show error toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(comment.content);
  };

  const getStatusColor = () => {
    switch (comment.status) {
      case ECommentStatus.APPROVED:
        return '#10B981'; // green
      case ECommentStatus.REJECTED:
        return '#EF4444'; // red
      case ECommentStatus.PENDING:
      default:
        return '#F59E0B'; // yellow
    }
  };

  const getStatusText = () => {
    switch (comment.status) {
      case ECommentStatus.APPROVED:
        return 'Approved';
      case ECommentStatus.REJECTED:
        return 'Rejected';
      case ECommentStatus.PENDING:
      default:
        return 'Pending';
    }
  };

  return (
    <View className="bg-[#030015] flex-col rounded-[16px] w-full mb-[4px]">
      <View className="flex-row h-[64px] items-center px-[16px] py-[16px]">
        <View className="flex-1 flex-row items-center gap-[6px]">
          <Text
            fontFamily="Geist"
            className="text-[#E7E5E4] text-[14px] font-[600] tracking-[-0.28px] leading-[14px]"
          >
            {comment.created_by}
          </Text>
          <Text
            fontFamily="Geist"
            className="text-[#78716C] text-[12px] font-[400] tracking-[-0.24px] leading-[normal]"
          >
            {moment(comment.created_at).format('DD/MM/YY HH:mm')}
          </Text>

          {/* Status Badge */}
          <View
            className="px-[8px] py-[2px] rounded-[4px]"
            style={{backgroundColor: `${getStatusColor()}20`}}
          >
            <Text
              fontFamily="Geist"
              className="text-[10px] font-[500] uppercase"
              style={{color: getStatusColor()}}
            >
              {getStatusText()}
            </Text>
          </View>
        </View>
      </View>

      {/* Comment Content */}
      <View className="flex-col gap-[8px] px-[16px] pb-[16px]">
        {isEditing ? (
          <View className="bg-[#010009] rounded-[8px] mb-[8px]">
            <TextInput
              value={editText}
              onChangeText={setEditText}
              placeholder="Edit comment"
              placeholderTextColor="#A8A29E"
              multiline
              className="px-[16px] py-[12px] rounded-lg text-[#FAFAF9] text-[15px] font-[400] tracking-[-0.3px] leading-[1.35]"
              style={{
                fontFamily: 'Geist',
                textAlignVertical: 'top',
                minHeight: 80
              }}
            />
          </View>
        ) : (
          <Text
            fontFamily="Geist"
            className="text-[#FAFAF9] text-[15px] font-[400] tracking-[-0.3px] leading-[1.35] w-[361px]"
          >
            {comment.content}
          </Text>
        )}

        {/* Status Details */}
        {comment.status === ECommentStatus.APPROVED && comment.approved_by && (
          <Text
            fontFamily="Geist"
            className="text-[#10B981] text-[12px] font-[400] tracking-[-0.24px] leading-[normal]"
          >
            Approved by {comment.approved_by} on{' '}
            {moment(comment.approved_at).format('DD/MM/YY HH:mm')}
          </Text>
        )}

        {comment.status === ECommentStatus.REJECTED && comment.rejected_by && (
          <Text
            fontFamily="Geist"
            className="text-[#EF4444] text-[12px] font-[400] tracking-[-0.24px] leading-[normal]"
          >
            Rejected by {comment.rejected_by} on{' '}
            {moment(comment.rejected_at).format('DD/MM/YY HH:mm')}
          </Text>
        )}
      </View>

      {/* Comment Actions */}
      <View className="flex-row h-[48px] items-center justify-between px-0 py-0 w-full">
        <TouchableOpacity className="flex-row gap-[6px] h-full items-center px-[16px] py-0">
          <svg width={20} height={20} viewBox="0 0 20 20" fill="none">
            <path
              d="M5.835 18.33V9.165m-4.167 1.667v5.833c0 .92.746 1.667 1.667 1.667h11.188a2.5 2.5 0 002.471-2.12l.898-5.833a2.5 2.5 0 00-2.471-2.88H12.5a.833.833 0 01-.833-.834V3.72c0-1.135-.92-2.055-2.055-2.055-.27 0-.516.16-.626.407L6.055 8.669a.833.833 0 01-.762.495H3.335c-.92 0-1.667.746-1.667 1.667z"
              stroke="#A8A29E"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Text
            fontFamily="Geist"
            className="text-[#A8A29E] text-[14px] font-[400] leading-[20px]"
          >
            {Object.keys(comment?.likes ?? {}).length}
          </Text>
        </TouchableOpacity>
      </View>
      <View className="flex-row items-center justify-between gap-[8px] px-[8px] mb-[8px]">
        {/* Admin Actions for Pending Comments */}
        {canManage &&
        (comment.status === ECommentStatus.PENDING || !comment?.status) ? (
          <View className="flex-row items-center gap-[8px]">
            <TouchableOpacity
              className="px-[12px] py-[6px] bg-[#10B98120] rounded-[6px]"
              onPress={handleApprove}
              disabled={isProcessing}
            >
              <Text
                fontFamily="Geist"
                className="text-[#10B981] text-[14px] font-[500] tracking-[-0.28px]"
              >
                Accept
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              className="px-[12px] py-[6px] bg-[#EF444420] rounded-[6px]"
              onPress={handleReject}
              disabled={isProcessing}
            >
              <Text
                fontFamily="Geist"
                className="text-[#EF4444] text-[14px] font-[500] tracking-[-0.28px]"
              >
                Reject
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View />
        )}
        <View className="flex-row items-center gap-[8px]">
          {isEditing ? (
            <>
              <TouchableOpacity
                className="px-[12px] py-[6px] bg-[#10B98120] rounded-[6px]"
                onPress={handleSaveEdit}
                disabled={isProcessing || !editText.trim()}
              >
                <Text
                  fontFamily="Geist"
                  className={classNames(
                    'text-[14px] font-[500] tracking-[-0.28px]',
                    {
                      'text-[#10B981]': editText.trim() && !isProcessing,
                      'text-[#44403C]': !editText.trim() || isProcessing
                    }
                  )}
                >
                  {isProcessing ? 'Saving...' : 'Save'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className="px-[12px] py-[6px] bg-[#78716C20] rounded-[6px]"
                onPress={handleCancelEdit}
                disabled={isProcessing}
              >
                <Text
                  fontFamily="Geist"
                  className="text-[#78716C] text-[14px] font-[500] tracking-[-0.28px]"
                >
                  Cancel
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              {canEdit && (
                <TouchableOpacity
                  className="px-[16px] py-[8px]"
                  onPress={handleEdit}
                >
                  <Text
                    fontFamily="Geist"
                    className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px] leading-[16px] text-center"
                  >
                    Edit
                  </Text>
                </TouchableOpacity>
              )}
              {isAdmin && !preview && (
                <TouchableOpacity
                  className="px-[12px] py-[6px] bg-[#DC262620] rounded-[6px]"
                  onPress={() => setShowDeleteConfirm(true)}
                  disabled={isProcessing}
                >
                  <Text
                    fontFamily="Geist"
                    className="text-[#DC2626] text-[14px] font-[500] tracking-[-0.28px]"
                  >
                    Remove
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </View>
      {showDeleteConfirm && (
        <ConfirmDeleteModal
          title="Confirm Delete"
          description="Please note that this action cannot be undone."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleRemove}
        />
      )}
    </View>
  );
};

export const NoteDetailComments = ({
  noteId,
  noteItem,
  preview
}: NoteDetailCommentsProps) => {
  const refreshComment = useAirportNote((state) => state.refreshComment);
  const setCommentPanelVisible = useAirportNote(
    (state) => state.setCommentPanelVisible
  );
  const setRefreshComment = useAirportNote((state) => state.setRefreshComment);
  const airportNoteInfo = useAirportNote((state) => state.airportNoteInfo);
  const [comments, setComments] = useState<ICommentInfo[]>([]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!airportNoteInfo?.icao_code || !noteItem) return;
      try {
        const commentsData = await getCommentsByNoteId(noteId);
        setComments(commentsData);

        // Validate and update comment count if necessary
        await validateAndUpdateCommentCount(airportNoteInfo.icao_code, noteId);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setComments([]);
      }
    };

    void fetchComments();
  }, [noteId, refreshComment, airportNoteInfo?.icao_code, noteItem]);

  const handleEditComment = (comment: ICommentInfo) => {
    setCommentPanelVisible(comment);
  };

  const handleRefreshComments = () => {
    setRefreshComment();
  };

  return (
    <>
      <View className="flex-1">
        <ScrollView
          ref={(ref) => {
            (window as any).airportNoteCommentScrollRef = ref;
          }}
          showsVerticalScrollIndicator={false}
        >
          <View className="h-[48px] flex-row items-center px-0 py-0">
            <TouchableOpacity className="flex-row items-center gap-[6px] pl-[16px] pr-0 py-[12px] rounded-lg">
              <Text
                fontFamily="Inter"
                className="text-[#FAFAF9] text-[16px] font-[500] leading-[20px]"
              >
                {comments.length > 0
                  ? `${comments.length} Comments`
                  : 'Comments'}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Comments List */}
          <View className="px-[16px] flex-grow">
            {/* Helper Text */}
            {!preview && (
              <View className="pb-[20px]">
                <Text
                  fontFamily="Geist"
                  className="text-[#E7E5E4] text-[14px] font-[400] tracking-[-0.28px] leading-[20px] flex-1"
                >
                  Please share if there is any update or if this note is
                  outdated.
                </Text>
              </View>
            )}
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                noteItem={noteItem!}
                onEdit={handleEditComment}
                onRefresh={handleRefreshComments}
                preview={preview}
              />
            ))}
          </View>
          {!preview && <View className="h-[100px]" />}
        </ScrollView>
      </View>
    </>
  );
};
